import Bottle, { type IContainer } from 'bottlejs'
import type { Constructor } from './common-types'

export enum DiLifetime {
  Singleton,
  Transient,
}

export interface BindImplementation<TIds extends string> {
  constructor: Constructor<any>
  dependencyIds: TIds[]
}

export type MultiIdMap<TId extends string> = Partial<Record<TId, TId[]>>

type BindOptions<TId extends string> = {
  multiBindIds?: TId[]
  dependencyIds?: TId[]
  lifetime?: DiLifetime
}

export class BindHelper<TId extends string> {
  public readonly multiIdToIds: MultiIdMap<TId> = {}

  constructor(
    private bottle: Bottle<TId>,
    private readonly overrideMap?: Record<string, BindImplementation<TId>>,
  ) {
    this.bottle = bottle
    this.overrideMap = overrideMap
  }

  public bindSelf<T>(
    ctor: Constructor<T>,
    { multiBindIds, dependencyIds = [], lifetime = DiLifetime.Singleton }: BindOptions<TId> = {},
  ): void {
    this.bind(ctor, ctor.name as TId, {
      multiBindIds,
      dependencyIds,
      lifetime,
    })
  }

  public bind<T>(
    ctor: Constructor<T>,
    bindId: TId,
    {
      multiBindIds = [],
      dependencyIds = [],
      lifetime = DiLifetime.Singleton,
    }: BindOptions<TId> = {},
  ): void {
    let bindImpl = { constructor: ctor, dependencyIds } as BindImplementation<TId>

    if (this.overrideMap) {
      for (const id of bindId) {
        if (this.overrideMap[id]) {
          bindImpl = this.overrideMap[id]
          break
        }
      }
    }

    switch (lifetime) {
      case DiLifetime.Singleton:
        this.bottle.service(bindId, bindImpl.constructor, ...bindImpl.dependencyIds)
        break
      case DiLifetime.Transient:
        this.bottle.factory(bindId, (container: IContainer<TId>) => {
          return () => {
            const deps = bindImpl.dependencyIds.map((id) => container[id])
            return new bindImpl.constructor(...deps)
          }
        })
        break
    }

    for (const multiId of multiBindIds) {
      this.bindIdToMultiId(multiId, bindId)
    }
  }

  public bindInstance<T extends object>(instanceValue: T, bindId: TId) {
    this.bottle.factory(bindId, () => instanceValue)
  }

  public bindIdToMultiId(multiId: TId, bindId: TId): void {
    if (!this.multiIdToIds[multiId]) {
      this.multiIdToIds[multiId] = []
    }
    this.multiIdToIds[multiId].push(bindId)
  }
}
